/**
 * Company-scoped Attendance checklist templates + month completions (localStorage).
 * Mirrors payroll checklist UX until a dedicated API is added.
 */

const TEMPLATES_KEY = "pacehrm_attendance_checklists";
const COMPLETIONS_KEY = "pacehrm_attendance_checklist_completions";

function companyId() {
  try {
    const raw = localStorage.getItem("companyData");
    const data = raw ? JSON.parse(raw) : null;
    return String(data?.id || data?.companyId || "default");
  } catch {
    return "default";
  }
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

function templatesBucket() {
  const all = readJson(TEMPLATES_KEY, {});
  return Array.isArray(all[companyId()]) ? all[companyId()] : [];
}

function saveTemplates(rows) {
  const all = readJson(TEMPLATES_KEY, {});
  all[companyId()] = rows;
  writeJson(TEMPLATES_KEY, all);
}

function completionsBucket() {
  const all = readJson(COMPLETIONS_KEY, {});
  const cid = companyId();
  if (!all[cid] || typeof all[cid] !== "object") all[cid] = {};
  return { all, cid, byMonth: all[cid] };
}

/** Frequency helpers aligned with payroll checklist editor. */
export function checklistDueInMonth(checklist, payMonth) {
  if (!checklist || checklist.active === false) return false;
  const freq = checklist.frequency || "monthly";
  const meta = checklist.specificMonthsJson || {};
  const ym = String(payMonth || "").slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(ym)) return false;
  const monthNum = Number(ym.slice(5, 7));

  if (freq === "monthly" || freq === "once_per_month" || freq === "recurring") return true;
  if (freq === "one_time") return meta.oneTimeMonth === ym;
  if (["selected_months", "quarterly", "half_yearly"].includes(freq)) {
    const months = Array.isArray(meta.months) ? meta.months.map(Number) : [];
    return months.includes(monthNum);
  }
  if (freq === "yearly") return Number(meta.month || 3) === monthNum;
  if (freq === "date_range") {
    const from = meta.fromMonth || "";
    const to = meta.toMonth || "";
    if (from && ym < from) return false;
    if (to && ym > to) return false;
    return !!(from || to);
  }
  return true;
}

export function listAttendanceChecklists(payMonth = null) {
  const rows = templatesBucket().slice().sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  if (!payMonth) return rows;
  return rows.filter((c) => checklistDueInMonth(c, payMonth));
}

export function saveAttendanceChecklist(draft) {
  const rows = templatesBucket();
  const now = new Date().toISOString();
  if (draft.id) {
    const idx = rows.findIndex((r) => r.id === draft.id);
    if (idx < 0) throw new Error("Checklist not found");
    rows[idx] = { ...rows[idx], ...draft, updatedAt: now };
  } else {
    rows.push({
      ...draft,
      id: `acl_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      createdAt: now,
      updatedAt: now,
    });
  }
  saveTemplates(rows);
  return rows.find((r) => r.id === (draft.id || rows[rows.length - 1].id));
}

export function deleteAttendanceChecklist(id) {
  saveTemplates(templatesBucket().filter((r) => r.id !== id));
}

export function getAttendanceChecklistCompletions(payMonth) {
  const { byMonth } = completionsBucket();
  const list = byMonth[payMonth];
  return Array.isArray(list) ? list : [];
}

export function upsertAttendanceChecklistCompletion({
  checklistId,
  payMonth,
  itemKey,
  completed,
}) {
  const { all, cid, byMonth } = completionsBucket();
  const list = Array.isArray(byMonth[payMonth]) ? [...byMonth[payMonth]] : [];
  const idx = list.findIndex(
    (c) => c.checklistId === checklistId && c.itemKey === itemKey
  );
  const row = {
    checklistId,
    itemKey,
    completed: !!completed,
    completedAt: completed ? new Date().toISOString() : null,
  };
  if (idx >= 0) list[idx] = { ...list[idx], ...row };
  else list.push(row);
  byMonth[payMonth] = list;
  all[cid] = byMonth;
  writeJson(COMPLETIONS_KEY, all);
  return row;
}

/** Processed employee ids for Process Attendance step (per company + month). */
const PROCESSED_KEY = "pacehrm_attendance_processed";

export function getAttendanceProcessedIds(payMonth) {
  try {
    const all = readJson(PROCESSED_KEY, {});
    const cid = companyId();
    const set = all?.[cid]?.[payMonth];
    return Array.isArray(set) ? set.map(String) : [];
  } catch {
    return [];
  }
}

export function setAttendanceProcessedIds(payMonth, ids) {
  const all = readJson(PROCESSED_KEY, {});
  const cid = companyId();
  if (!all[cid]) all[cid] = {};
  all[cid][payMonth] = [...new Set((ids || []).map(String))];
  writeJson(PROCESSED_KEY, all);
}
